import { useState, useEffect, useCallback } from "react";
import { Button, Typography, message, Tabs, Upload, Form, Input, Progress, Tag, Card, Row, Col } from "antd";
import {
  UserOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  InboxOutlined,
  LoadingOutlined,
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
  TrophyOutlined,
  DownloadOutlined
} from "@ant-design/icons";

import { motion } from "framer-motion";
import Layout from "../../components/layout/Layout";
import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../services/apiService";
import bountyService from "../../services/bountyService";
import BountySystem from "../../components/BountySystem";
import "../box/BoxPage.css";

const { Title, Text, Paragraph } = Typography;

const UploadPage = () => {
  const { isAuthenticated, user, hasPermission, walletAddress } = useAuth();



  const [activeTab, setActiveTab] = useState("upload");
  const [myPapers, setMyPapers] = useState([]);
  const [uploadStats, setUploadStats] = useState(null);



  // Load user uploaded papers from API - ONLY papers with DOI
  const loadMyPapers = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      console.log("Loading user uploaded papers from API...");

      // Get user wallet address or user ID for API call
      const userWallet = walletAddress || user?.wallet_address || user?.publicKey;
      const userId = user?.id || user?.user_id;

      let apiPapers = [];
      let apiMetadata = null;

      // Try to fetch from API using wallet address first
      if (userWallet) {
        try {
          const response = await fetch(`https://api.scai.sh/api/user-uploads/${userWallet}`);
          if (response.ok) {
            const apiData = await response.json();
            if (apiData.success && apiData.data) {
              apiMetadata = apiData.metadata; // Store metadata for statistics
              apiPapers = apiData.data.map(paper => ({
                id: paper.id,
                txId: paper.irys_tx_id,
                url: paper.irys_url,
                title: paper.title,
                doi: paper.doi,
                author: paper.authors,
                year: paper.metadata?.year || new Date().getFullYear(),
                abstract: paper.metadata?.abstract || '',
                uploadDate: paper.created_at,
                lastAccessed: paper.last_accessed,
                accessCount: paper.access_count,
                fileSize: paper.file_size,
                contentType: paper.content_type,
                status: paper.status,
                metadata: {
                  ...paper.metadata,
                  fileType: 'literature',
                  type: 'paper-metadata'
                },
                source: paper.source || 'api',
                isOA: paper.is_oa,
                uploaderUserId: paper.uploader_user_id,
                uploaderWallet: paper.uploader_wallet,
                userType: paper.user_type
              }));
              console.log(`Loaded ${apiPapers.length} papers from API`);

              // Store metadata for display
              setUploadStats(apiMetadata);
            }
          }
        } catch (apiError) {
          console.warn("Failed to load from API, falling back to localStorage:", apiError);
        }
      }

      // Fallback to localStorage if API fails or no wallet address
      if (apiPapers.length === 0) {
        console.log("Falling back to localStorage...");
        const userIdForStorage = userId;
        const uploads = localStorage.getItem(`scai_uploads_${userIdForStorage}`);
        const allUploads = uploads ? JSON.parse(uploads) : [];

        // Filter ONLY literature/paper type files (files with DOI) - Upload page shows ONLY papers
        apiPapers = allUploads.filter(file =>
          (file.metadata?.fileType === 'literature' ||
            file.metadata?.type === 'paper-metadata' ||
            file.metadata?.doi ||
            file.doi) && // Must have DOI
          !file.isLocal // Exclude local uploads (papers should be on Irys)
        );
        console.log(`Loaded ${apiPapers.length} papers from localStorage fallback`);
      }

      setMyPapers(apiPapers);
    } catch (error) {
      console.error("Error loading papers:", error);
      setMyPapers([]);
    }
  }, [isAuthenticated, user?.id, user?.user_id, user?.wallet_address, user?.publicKey, walletAddress]);

  // Initialize data
  useEffect(() => {
    if (isAuthenticated) {
      loadMyPapers();
    }
  }, [isAuthenticated, user?.id, loadMyPapers]);

  // Remove uploaded paper
  const handleRemovePaper = async (txId) => {
    try {
      // Note: Irys doesn't support deletion, but we can mark as deleted in our system
      message.warning("Papers stored on Irys network cannot be deleted, but will be hidden from your list");

      // Remove from local state
      setMyPapers(prev => prev.filter(paper => paper.txId !== txId));
    } catch (error) {
      console.error("Error removing paper:", error);
      message.error("Failed to remove paper");
    }
  };

  return (
    <Layout>
      <div className="box-page light-theme">
        {/* Hero Section */}
        <div className="hero-section1">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <Title level={1} className="hero-title" style={{ color: "#fff" }}>
              {isAuthenticated ? `Upload Research Papers` : "Welcome to SCAI Upload"}
            </Title>
            <Paragraph className="hero-subtitle">
              Upload research papers to Iry network permanently and earn rewards.
            </Paragraph>
          </motion.div>
        </div>

        {/* Main Content */}
        <div
          className="main-content"
          style={{
            padding: "2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            className="scholar-container"
            style={{
              width: "100%",
              padding: "2rem",
            }}
          >
            {!isAuthenticated ? (
              // Show login requirement prompt when not logged in
              <div
                style={{
                  textAlign: "center",
                }}
              >
                <UserOutlined
                  style={{
                    fontSize: 64,
                    color: "#fff",
                    marginBottom: 7,
                  }}
                />
                <Title
                  level={2}
                  style={{
                    color: "#fff",
                    marginBottom: 16,
                  }}
                >
                  Login Required
                </Title>
                <Paragraph
                  style={{
                    color: "#ccc",
                    fontSize: 16,
                    marginBottom: 24,
                    maxWidth: 500,
                    margin: "0 auto 24px",
                  }}
                >
                  Please sign in to access your personalized Science workspace and manage your research materials.
                </Paragraph>

                <Button
                  className="modern-btn modern-btn-primary"
                  type="primary"
                  size="large"
                  style={{
                    height: 48,
                    paddingLeft: 32,
                    paddingRight: 32,
                    fontSize: 16,
                  }}
                  onClick={() => {
                    // Simulate clicking the login button in the top right corner
                    const loginButton = document.querySelector(".login-btn");
                    if (loginButton) {
                      loginButton.click();
                    } else {
                      // If login button is not found, show prompt message
                      message.info("Please use the login button in the top right corner to log in");
                    }
                  }}
                >
                  Sign In to Continue
                </Button>
              </div>
            ) : (
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                size="large"
                style={{ marginBottom: "2rem" }}
                className="scholar-tabs"
                items={[
                  {
                    key: 'upload',
                    label: (
                      <span>
                        <CloudUploadOutlined style={{ marginRight: "5px" }} />
                        Upload Papers
                      </span>
                    ),
                    children: (
                      <UploadTab
                        isAuthenticated={isAuthenticated}
                        hasPermission={hasPermission}
                        user={user}
                        onSuccess={loadMyPapers}
                      />
                    )
                  },
                  {
                    key: 'papers',
                    label: (
                      <span>
                        <FileTextOutlined style={{ marginRight: "5px" }} />
                        My Papers ({myPapers.length})
                      </span>
                    ),
                    children: (
                      <PapersTab
                        papers={myPapers}
                        onRemovePaper={handleRemovePaper}
                        uploadStats={uploadStats}
                      />
                    )
                  },
                  // TODO
                  // {
                  //   key: 'bounty',
                  //   label: (
                  //     <span>
                  //       <span style={{ marginRight: "5px" }}>🎯</span>
                  //       Bounty System
                  //     </span>
                  //   ),
                  //   children: (
                  //     <BountyTab
                  //       isAuthenticated={isAuthenticated}
                  //       user={user}
                  //     />
                  //   )
                  // }
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

// Upload Tab Component - Modern design with two modules
const UploadTab = ({ isAuthenticated, hasPermission, user, onSuccess }) => {
  if (!hasPermission('plus') && isAuthenticated) {
    return (
      <div className="upgrade-prompt" style={{
        background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
        border: "1px solid #bae6fd",
        borderRadius: "12px",
        padding: "3rem",
        textAlign: "center",
        margin: "2rem 0"
      }}>
        <div style={{ fontSize: "48px", marginBottom: "1rem" }}>🚀</div>
        <Title level={3} style={{ color: "#0369a1", marginBottom: "1rem" }}>
          Upgrade to Plus for Paper Upload
        </Title>
        <Paragraph style={{ color: "#0284c7", fontSize: "16px", marginBottom: "2rem" }}>
          Join our Plus membership to upload research papers to decentralized storage
        </Paragraph>
        <Button type="primary" size="large" style={{
          background: "#0ea5e9",
          borderColor: "#0ea5e9",
          borderRadius: "8px",
          padding: "12px 32px",
          height: "auto"
        }}>
          Upgrade Now
        </Button>
      </div>
    );
  }

  return (
    <div className="upload-tab-modern">
      <PaperUploadForm
        user={user}
        isAuthenticated={isAuthenticated}
        onSuccess={onSuccess}
      />
    </div>
  );
};

// Papers Tab Component - Modern design with dashboard and list
const PapersTab = ({ papers, onRemovePaper, uploadStats }) => {
  const handleViewDocument = (paper) => {
    if (paper.url) {
      window.open(paper.url, "_blank");
    } else if (paper.txId) {
      // Build URL using txId
      const url = `https://gateway.irys.xyz/${paper.txId}`;
      window.open(url, "_blank");
    } else {
      message.error("Document URL not available");
    }
  };

  // 🆕 导出 CSV 功能
  const handleExportCSV = () => {
    if (papers.length === 0) {
      message.warning("No papers to export");
      return;
    }

    try {
      // CSV 表头
      const headers = [
        "Title",
        "DOI",
        "Authors",
        "Year",
        "Upload Date",
        "Irys TX ID",
        "Irys URL",
        "File Size (Bytes)",
        "Access Count",
        "Is Open Access",
        "Status",
        "Abstract",
        "Uploader User ID",
        "Uploader Wallet",
        "Source"
      ];

      // 转换数据为 CSV 行
      const csvRows = papers.map(paper => {
        return [
          `"${(paper.title || paper.metadata?.title || paper.fileName || "Untitled").replace(/"/g, '""')}"`,
          `"${(paper.doi || "").replace(/"/g, '""')}"`,
          `"${(paper.author || paper.metadata?.authors || "").replace(/"/g, '""')}"`,
          `"${paper.year || paper.metadata?.year || ""}"`,
          `"${paper.uploadDate || ""}"`,
          `"${paper.txId || ""}"`,
          `"${paper.url || (paper.txId ? `https://gateway.irys.xyz/${paper.txId}` : "")}"`,
          `"${paper.fileSize || 0}"`,
          `"${paper.accessCount || 0}"`,
          `"${paper.isOA ? "Yes" : "No"}"`,
          `"${paper.status || ""}"`,
          `"${(paper.abstract || paper.metadata?.abstract || "").replace(/"/g, '""')}"`,
          `"${paper.uploaderUserId || ""}"`,
          `"${paper.uploaderWallet || ""}"`,
          `"${paper.source || ""}"`,
        ].join(",");
      });

      // 组合 CSV 内容
      const csvContent = [
        headers.join(","),
        ...csvRows
      ].join("\n");

      // 创建 Blob 并下载
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" }); // \uFEFF 是 BOM，确保 Excel 正确识别 UTF-8
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", `my_papers_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Successfully exported ${papers.length} papers to CSV`);
    } catch (error) {
      console.error("Export CSV error:", error);
      message.error("Failed to export CSV: " + error.message);
    }
  };

  // Calculate dashboard stats from API metadata and papers
  const totalPapers = uploadStats?.total_papers || papers.length;
  const oaPapers = uploadStats?.oa_papers || papers.filter(p => p.isOA).length;
  const nonOaPapers = uploadStats?.non_oa_papers || papers.filter(p => !p.isOA).length;
  const totalSize = papers.reduce((sum, paper) => sum + (paper.fileSize || 0), 0);
  const totalAccessCount = papers.reduce((sum, paper) => sum + (paper.accessCount || 0), 0);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleDateString();
  };

  // TODO
  return (
    <div className="papers-tab-modern">
      {/* TODO Dashboard Module */}
      {/* <div className="dashboard-module" style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "2rem",
        marginBottom: "2rem",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid #f0f0f0"
      }}>
        <Title level={4} style={{ marginBottom: "1.5rem", color: "#1a1a1a" }}>
          📊 My Research Dashboard
        </Title>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1.5rem" }}>
          <div className="stat-card" style={{
            background: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)",
            padding: "1.5rem",
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📚</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#0369a1" }}>{totalPapers}</div>
            <div style={{ color: "#0284c7", fontSize: "14px" }}>Total Papers</div>
          </div>

          <div className="stat-card" style={{
            background: "linear-gradient(135deg, #f0fdf4 0%, #f7fee7 100%)",
            padding: "1.5rem",
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>�</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#166534" }}>{oaPapers}</div>
            <div style={{ color: "#15803d", fontSize: "14px" }}>Open Access</div>
          </div>

          <div className="stat-card" style={{
            background: "linear-gradient(135deg, #fef3c7 0%, #fef7cd 100%)",
            padding: "1.5rem",
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>🔒</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#92400e" }}>{nonOaPapers}</div>
            <div style={{ color: "#a16207", fontSize: "14px" }}>Restricted Access</div>
          </div>

          <div className="stat-card" style={{
            background: "linear-gradient(135deg, #fce7f3 0%, #fdf2f8 100%)",
            padding: "1.5rem",
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>👁️</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#be185d" }}>{totalAccessCount}</div>
            <div style={{ color: "#c2185b", fontSize: "14px" }}>Total Views</div>
          </div>
        </div>
      </div> */}

      {/* Papers List Module */}
      <div className="papers-list-module" style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "2rem",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid #f0f0f0"
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem"
        }}>
          <Title level={4} style={{ margin: 0, color: "#1a1a1a" }}>
            📄 My Papers
          </Title>

          {/* 🆕 导出 CSV 按钮 */}
          {papers.length > 0 && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              style={{
                background: "#0ea5e9",
                borderColor: "#0ea5e9",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              Export CSV
            </Button>
          )}
        </div>

        {papers.length === 0 ? (
          <div style={{
            textAlign: "center",
            padding: "3rem 2rem",
            color: "#999"
          }}>
            <FileTextOutlined style={{ fontSize: "48px", color: "#d9d9d9", marginBottom: "1rem" }} />
            <Title level={4} style={{ color: "#999", marginBottom: "0.5rem" }}>
              No papers uploaded yet
            </Title>
            <Paragraph style={{ color: "#666", fontSize: "14px" }}>
              Upload your first research paper to get started
            </Paragraph>
          </div>
        ) : (
          <div className="papers-list">
            {papers.map((paper, index) => (
              <div key={paper.txId || index} className="paper-item" style={{
                display: "flex",
                alignItems: "center",
                padding: "1rem",
                border: "1px solid #f0f0f0",
                borderRadius: "8px",
                marginBottom: "0.5rem",
                transition: "all 0.2s ease",
                cursor: "pointer"
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#40B5F7";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(64,181,247,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#f0f0f0";
                  e.currentTarget.style.boxShadow = "none";
                }}
                onClick={() => handleViewDocument(paper)}
              >
                <div style={{ marginRight: "1rem", fontSize: "24px" }}>📄</div>

                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "600", color: "#1a1a1a", marginBottom: "0.25rem" }}>
                    {paper.title || paper.metadata?.title || paper.fileName || "Untitled Paper"}
                  </div>

                  <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem", flexWrap: "wrap" }}>
                    {paper.doi && (
                      <Tag color="blue" size="small">
                        DOI: {paper.doi}
                      </Tag>
                    )}
                    {paper.isOA !== undefined && (
                      <Tag color={paper.isOA ? "green" : "orange"} size="small">
                        {paper.isOA ? "Open Access" : "Restricted"}
                      </Tag>
                    )}
                    {paper.status && (
                      <Tag color="default" size="small">
                        {paper.status}
                      </Tag>
                    )}
                  </div>

                  <div style={{ fontSize: "12px", color: "#666", lineHeight: "1.4" }}>
                    {paper.author && (
                      <div>Authors: {paper.author}</div>
                    )}
                    <div style={{ display: "flex", gap: "1rem", marginTop: "0.25rem" }}>
                      {paper.uploadDate && (
                        <span>Uploaded: {formatDate(paper.uploadDate)}</span>
                      )}
                      {paper.accessCount > 0 && (
                        <span>Views: {paper.accessCount}</span>
                      )}
                      {paper.year && (
                        <span>Year: {paper.year}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Button
                    icon={<EyeOutlined />}
                    size="small"
                    type="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDocument(paper);
                    }}
                  />
                  <Button
                    icon={<DeleteOutlined />}
                    size="small"
                    type="text"
                    danger
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemovePaper(paper.txId);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Paper Upload Form Component - Direct form without modal
const PaperUploadForm = ({ user, isAuthenticated, onSuccess }) => {
  // 🆕 在组件内部获取 walletAddress
  const { walletAddress } = useAuth();

  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileList, setFileList] = useState([]);
  const [doiLoading, setDoiLoading] = useState(false);
  const [paperMetadata, setPaperMetadata] = useState(null);

  // DOI search function
  const handleDoiSearch = async (doi) => {
    if (!doi || !doi.trim()) {
      setPaperMetadata(null);
      return;
    }

    setDoiLoading(true);
    try {
      const data = await apiService.getPaperInfo(doi.trim());

      if (data && !data.error) {
        setPaperMetadata(data);
        // Auto-fill form fields
        form.setFieldsValue({
          title: data.title,
          description: data.abstract && data.abstract !== "Abstract Not Available"
            ? data.abstract.substring(0, 200) + "..."
            : "Paper abstract",
          authors: data.author,
          year: data.year,
        });
        message.success("DOI information retrieved successfully");
      } else {
        setPaperMetadata(null);
        message.warning(data?.error || "DOI information not found");
      }
    } catch (error) {
      console.error("DOI search error:", error);
      setPaperMetadata(null);
      message.error("DOI query error: " + error.message);
    } finally {
      setDoiLoading(false);
    }
  };

  const handleUpload = async (values) => {
    if (fileList.length === 0) {
      message.error("Please select a file to upload");
      return;
    }

    if (!isAuthenticated) {
      message.error("Please login or register before uploading files");
      return;
    }

    if (!values.doi) {
      message.error("DOI is required for paper upload");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const file = fileList[0];
      const actualFile = file.originFileObj || file;

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Upload paper using dedicated API
      const uploadResult = await apiService.uploadPaperWithFile(
        actualFile,
        values.doi,
        "manual", // source
        {
          title: values.title,
          description: values.description || "",
          authors: values.authors || paperMetadata?.author,
          year: values.year || paperMetadata?.year,
          abstract: paperMetadata?.abstract,
          isPrivate: values.isPrivate || false,
          userId: user?.id || user?.user_id || "anonymous",
          walletAddress: walletAddress || user?.wallet_address || null, // 🆕 添加钱包地址
          email: user?.email || null, // 🆕 添加邮箱
          paperMetadata: paperMetadata,
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Upload failed');
      }

      message.success("Paper uploaded successfully!");

      // Reset form
      form.resetFields();
      setFileList([]);
      setPaperMetadata(null);
      setUploadProgress(0);

      // Refresh papers list
      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error("Upload error:", error);
      message.error("Upload failed: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      // Validate file type
      const isValidType = file.type === 'application/pdf' ||
        file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'text/plain';

      if (!isValidType) {
        message.error('Please upload PDF, Word, or text files only');
        return false;
      }

      // Validate file size (50MB limit)
      const isValidSize = file.size / 1024 / 1024 < 50;
      if (!isValidSize) {
        message.error('File must be smaller than 50MB');
        return false;
      }

      setFileList([file]);
      return false; // Prevent auto upload
    },
    fileList,
    onRemove: () => {
      setFileList([]);
    },
  };

  return (
    <div className="upload-form-modern">
      {/* Paper Metadata Module */}
      {paperMetadata ? (
        <div className="metadata-module" style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "2rem",
          marginBottom: "2rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          border: "1px solid #f0f0f0"
        }}>
          <Title level={4} style={{ marginBottom: "1.5rem", color: "#1a1a1a" }}>
            📄 Paper Information
          </Title>

          <div style={{
            background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
            border: "1px solid #bae6fd",
            borderRadius: "8px",
            padding: "1.5rem",
            marginBottom: "1rem"
          }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
              <span style={{ fontSize: "20px", marginRight: "8px" }}>✅</span>
              <Text strong style={{ color: "#0369a1" }}>Paper information retrieved successfully</Text>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <Text strong style={{ color: "#0284c7" }}>Title:</Text>
                <div style={{ marginTop: "4px", fontSize: "14px" }}>{paperMetadata.title}</div>
              </div>
              <div>
                <Text strong style={{ color: "#0284c7" }}>Authors:</Text>
                <div style={{ marginTop: "4px", fontSize: "14px" }}>{paperMetadata.author}</div>
              </div>
              <div>
                <Text strong style={{ color: "#0284c7" }}>Year:</Text>
                <div style={{ marginTop: "4px", fontSize: "14px" }}>{paperMetadata.year}</div>
              </div>
              <div>
                <Text strong style={{ color: "#0284c7" }}>DOI:</Text>
                <div style={{ marginTop: "4px", fontSize: "14px" }}>{paperMetadata.doi}</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="doi-prompt-module" style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "2rem",
          marginBottom: "2rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          border: "1px solid #f0f0f0",
          textAlign: "center"
        }}>
          <div style={{ fontSize: "48px", marginBottom: "1rem" }}>🔍</div>
          <Title level={4} style={{ color: "#1a1a1a", marginBottom: "1rem" }}>
            Enter DOI to Get Started
          </Title>
          <Paragraph style={{ color: "#666", marginBottom: "0" }}>
            Please enter the paper's DOI below to automatically retrieve paper information
          </Paragraph>
        </div>
      )}

      {/* Upload Form Module */}
      <div className="upload-form-module" style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "2rem",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid #f0f0f0"
      }}>
        <Title level={4} style={{ marginBottom: "1.5rem", color: "#1a1a1a" }}>
          📤 Upload Paper
        </Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpload}
        >
          <Form.Item
            label="DOI"
            name="doi"
            rules={[{ required: true, message: "Please enter the paper's DOI" }]}
            extra="Enter the DOI to automatically retrieve paper information"
          >
            <Input
              placeholder="10.1000/example.doi"
              onBlur={(e) => handleDoiSearch(e.target.value)}
              suffix={doiLoading ? <LoadingOutlined /> : <SearchOutlined />}
            />
          </Form.Item>

          {paperMetadata && (
            <div style={{
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
              borderRadius: "6px",
              padding: "12px",
              marginBottom: "16px"
            }}>
              <Text strong style={{ color: "#52c41a" }}>✓ Paper information retrieved</Text>
              <br />
              <Text type="secondary">Title: {paperMetadata.title}</Text>
            </div>
          )}

          <Form.Item
            label="Paper Title"
            name="title"
            rules={[{ required: true, message: "Please enter the paper title" }]}
          >
            <Input placeholder="Enter paper title" />
          </Form.Item>

          <Form.Item
            label="Authors"
            name="authors"
          >
            <Input placeholder="Enter authors (optional)" />
          </Form.Item>

          <Form.Item
            label="Publication Year"
            name="year"
          >
            <Input placeholder="Enter publication year (optional)" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter paper description or abstract (optional)"
            />
          </Form.Item>

          <Form.Item
            label="Upload File"
            extra="Supports PDF, Word documents, or text files, maximum 50MB"
          >
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">
                Please upload the full-text document of the paper (PDF format recommended)
              </p>
            </Upload.Dragger>
          </Form.Item>

          {uploading && (
            <Form.Item>
              <Progress
                percent={uploadProgress}
                status={uploadProgress === 100 ? 'success' : 'active'}
              />
              <div style={{ textAlign: 'center', marginTop: '8px', color: '#666' }}>
                Uploading to Irys network...
              </div>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: "Left" }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={uploading}
              disabled={fileList.length === 0}
              className="modern-btn"
              size="large"
              style={{ color: "#fff" }}
            >
              <span style={{ color: "#fff" }}>Upload Paper</span>
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

// Bounty Tab Component
const BountyTab = ({ isAuthenticated, user }) => {
  if (!isAuthenticated) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <TrophyOutlined style={{ fontSize: '64px', color: '#faad14', marginBottom: '1rem' }} />
        <Title level={2}>SCAI Bounty System</Title>
        <Paragraph style={{ fontSize: '16px', maxWidth: '600px', margin: '0 auto 2rem' }}>
          Earn SCAI tokens by uploading papers that others need.
          Create bounties for papers you want to access.
        </Paragraph>
        <Button type="primary" size="large">
          Sign In to Access Bounties
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Bounty System Component */}
      <BountySystem />

      {/* How it Works Section */}
      <Card title="🎯 How SCAI Bounty System Works" style={{ marginTop: '2rem' }}>
        <Row gutter={24}>
          <Col span={12}>
            <div style={{
              background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
              border: "1px solid #bae6fd",
              borderRadius: "12px",
              padding: "1.5rem",
              height: '100%'
            }}>
              <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🎯</div>
              <Title level={4} style={{ color: "#0369a1", marginBottom: "1rem" }}>
                Create Bounties
              </Title>
              <Paragraph style={{ color: "#0284c7" }}>
                Need a specific paper? Create a bounty:
              </Paragraph>
              <ul style={{ color: "#0284c7", paddingLeft: '1rem' }}>
                <li>Specify the DOI you need</li>
                <li>Set reward (1,000-10,000 SCAI)</li>
                <li>Transfer SCAI to bounty wallet</li>
                <li>Wait for upload completion</li>
              </ul>
            </div>
          </Col>
          <Col span={12}>
            <div style={{
              background: "linear-gradient(135deg, #fef3c7 0%, #fef7cd 100%)",
              border: "1px solid #fde68a",
              borderRadius: "12px",
              padding: "1.5rem",
              height: '100%'
            }}>
              <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>💰</div>
              <Title level={4} style={{ color: "#92400e", marginBottom: "1rem" }}>
                Earn Rewards
              </Title>
              <Paragraph style={{ color: "#a16207" }}>
                Upload papers and earn automatically:
              </Paragraph>
              <ul style={{ color: "#a16207", paddingLeft: '1rem' }}>
                <li>Upload any paper with DOI</li>
                <li>System checks for bounties</li>
                <li>Automatic reward distribution</li>
                <li>Track earnings in dashboard</li>
              </ul>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};





export default UploadPage;
